import React, { Fragment, useRef, useState } from "react";
import { Grid } from "@mui/material";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import SettingsIcon from "@mui/icons-material/Settings";
import LoopTwoToneIcon from '@mui/icons-material/LoopTwoTone';
import MicIcon from '@mui/icons-material/Mic';
import { truncateString, icons } from "../constants";
import AudioRecordingModal from './AudioRecordingModal';

const QTDetailsAudioWithLabelSection = ({
  label,
  handleAudioModalOpen,
  editEnabled,
  details,
  setDetails,
  setUploadAudioFile,
  audioNameField = "originalAudioName",
  audioUrlField = "audioUrl", // Add this prop
  handleFileUpload,
  hideUploadButton = false, // Add this prop
  showProcessButton = false, // Add this prop
  onProcessAudio, // Add this prop
}) => {
  const [isRecordingModalOpen, setRecordingModalOpen] = useState(false);

  // Function to extract filename from URL
  const getFilenameFromUrl = (url) => {
    if (!url) return "";
    const urlParts = url.split('/');
    return urlParts[urlParts.length - 1];
  };

  const getAudioName = () => {
    const audioName = details?.[audioNameField];
    if (audioName) {
      return audioName;
    }
    const audioUrl = details?.[audioUrlField];
    if (audioUrl) {
      return getFilenameFromUrl(audioUrl);
    }
    return "";
  };
  const fileInputRef = useRef(null);

  const handleRecordingComplete = (audioBlob) => {
    // IMPORTANT (Safari): preserve the actual mime type produced by MediaRecorder.
    // Forcing `audio/wav` can lead to silent playback even when duration looks correct.
    const mimeType = audioBlob?.type || "audio/webm";
    
    // Debug logging to verify mime type in Safari
    console.log("🎤 Recording complete - audioBlob.type:", audioBlob?.type);
    console.log("🎤 Using mimeType:", mimeType);

    const extFromMime = (type) => {
      if (!type) return "webm";
      // Common MediaRecorder outputs:
      // - Safari: audio/mp4 (AAC) or audio/m4a
      // - Chrome: audio/webm (opus)
      // - Firefox: audio/ogg
      if (type.includes("mp4")) return "mp4";
      if (type.includes("m4a")) return "m4a";
      if (type.includes("aac")) return "aac";
      if (type.includes("ogg")) return "ogg";
      if (type.includes("wav")) return "wav";
      if (type.includes("webm")) return "webm";
      return "webm";
    };

    const title = details?.title?.trim();
    const safeBaseName = title
      ? title
      : `Untitled ${new Date()
          .toLocaleString()
          .replace(/[/\\?%*:|"<>]/g, "-")}`;

    const extension = extFromMime(mimeType);
    const fileName = `${safeBaseName}.${extension}`;

    const file = new File([audioBlob], fileName, { type: mimeType });
    const fileData = {
      preview: URL.createObjectURL(file),
      raw: file,
    };

    setUploadAudioFile(fileData);
    setDetails({ ...details, [audioNameField]: file.name });

    if (handleFileUpload) {
      handleFileUpload(fileData, undefined, "record");
    }
  };

  const handleFileInputChange = (event) => {
    const file = event.target.files[0];
    if (file) {
      const fileData = {
        preview: URL.createObjectURL(file),
        raw: file,
      };
      setUploadAudioFile(fileData);
      setDetails({ ...details, [audioNameField]: file.name });
      if (handleFileUpload) {
        handleFileUpload(fileData);
      }
    }
  };
  const handleFileUploadClick = () => {
    fileInputRef.current.click();
  };
  const audioUrl = details?.[audioUrlField];

  return (
    <Grid container alignItems={"center"} style={{ marginBottom: 24, minHeight: 40 }}>
      <span style={titleTextStyle}>{label}</span>
      {audioUrl ? (
        <Fragment>
          <PlayCircleFilledWhiteOutlinedIcon
            fontSize="large"
            onClick={handleAudioModalOpen}
            style={{ color: "#00A7CD", cursor: "pointer" }}
          />
          <span style={subTitleTextStyle}>
            {getAudioName()}
          </span>
        </Fragment>
      ) : (
        <span style={subTitleTextStyle}>No audio</span>
      )}
      {editEnabled && !hideUploadButton && (
        <Fragment>
          <input
            type="file"
            style={{ display: "none" }}
            accept="audio/*"
            ref={fileInputRef}
            onChange={handleFileInputChange}
          />
          <img
            src={icons.UploadIcon}
            alt="upload icon"
            style={uploadIconStyles}
            onClick={handleFileUploadClick}
          />
          <MicIcon
            fontSize="medium"
            onClick={() => setRecordingModalOpen(true)}
            style={micIconStyles}
            titleAccess="Record Audio"
          />
        </Fragment>
      )}
      {showProcessButton && editEnabled && audioUrl && (
        <LoopTwoToneIcon
          fontSize="medium"
          onClick={onProcessAudio}
          style={processIconStyles}
          titleAccess="Process Original Audio"
        />
      )}
      <AudioRecordingModal
        open={isRecordingModalOpen}
        handleClose={() => setRecordingModalOpen(false)}
        onRecordingComplete={handleRecordingComplete}
      />
    </Grid>
  );
};

export default QTDetailsAudioWithLabelSection;

const titleTextStyle = {
  width: "25%",
  color: "#020303",
  fontSize: 14,
  fontWeight: 700,
  fontFamily: "Avenir Book, sans-serif",
};
const uploadIconStyles = {
  width: 24,
  height: 24,
  cursor: "pointer",
  marginLeft: 30,
};

const processIconStyles = {
  color: "#00A7CD",
  cursor: "pointer",
  marginLeft: 15,
  fontSize: 24,
};

const micIconStyles = {
  color: "#00A7CD",
  cursor: "pointer",
  marginLeft: 15,
  fontSize: 28,
};

const subTitleTextStyle = {
  // width: "25%",
  color: "#000",
  // color: "#00A7CD",
  fontSize: 14,
  // fontWeight: 700,
  marginLeft: 30,
  fontFamily: "Avenir Book, sans-serif",
};
